import { LitElement, html, nothing } from 'lit';
import { formControlStyles } from './shared-styles';

let __tfFieldUid = 0;
const nextUid = () => `tf-field-${++__tfFieldUid}`;

export class TfFormControl extends LitElement {
  static formAssociated = true;

  static properties = {
    name: { type: String, reflect: true },
    label: { type: String },
    helperText: { type: String, attribute: 'helper-text' },
    errorText: { type: String, attribute: 'error-text' },
    required: { type: Boolean, reflect: true },
    disabled: { type: Boolean, reflect: true },
    invalid: { type: Boolean, reflect: true },
    autocomplete: { type: String },
    placeholder: { type: String },
    inputId: { type: String, attribute: 'input-id' },
    value: { type: String },
  };

  static get styles() {
    return [formControlStyles];
  }

  constructor() {
    super();
    this._internals = this.attachInternals();
    this._uid = nextUid();
    this.value = '';
    this.required = false;
    this.disabled = false;
    this.invalid = false;
  }

  get _controlId() {
    return this.inputId || this._uid;
  }

  get _helperId() {
    return `${this._controlId}-helper`;
  }

  get _errorId() {
    return `${this._controlId}-error`;
  }

  get _describedBy() {
    const ids = [];
    if (this.helperText) ids.push(this._helperId);
    if (this.invalid && this.errorText) ids.push(this._errorId);
    return ids.length ? ids.join(' ') : null;
  }

  get form() {
    return this._internals.form;
  }

  get validity() {
    return this._internals.validity;
  }

  get validationMessage() {
    return this._internals.validationMessage;
  }

  checkValidity() {
    return this._internals.checkValidity();
  }

  reportValidity() {
    return this._internals.reportValidity();
  }

  _syncFormValue() {
    this._internals.setFormValue(this.value ?? '');
    this._syncValidity();
  }

  _syncValidity() {
    if (this.required && !this.value) {
      this._internals.setValidity(
        { valueMissing: true },
        this.errorText || 'This field is required.'
      );
    } else if (this.invalid && this.errorText) {
      this._internals.setValidity({ customError: true }, this.errorText);
    } else {
      this._internals.setValidity({});
    }
  }

  updated(changed) {
    if (
      changed.has('value') ||
      changed.has('required') ||
      changed.has('invalid') ||
      changed.has('errorText')
    ) {
      this._syncFormValue();
    }
  }

  _renderLabel() {
    if (!this.label) return nothing;
    return html`
      <label class="field__label" for=${this._controlId}>
        ${this.label}${this.required
          ? html`
              <span class="field__required-marker" aria-hidden="true">*</span>
            `
          : nothing}
      </label>
    `;
  }

  _renderHelper() {
    if (!this.helperText) return nothing;
    return html`
      <span class="field__helper" id=${this._helperId}>
        ${this.helperText}
      </span>
    `;
  }

  _renderError() {
    if (!this.invalid || !this.errorText) return nothing;
    return html`
      <span class="field__error" id=${this._errorId} role="alert">
        ${this.errorText}
      </span>
    `;
  }

  _renderErrorIcon() {
    return html`
      <span class="field__error-icon" aria-hidden="true">!</span>
    `;
  }

  formDisabledCallback(disabled) {
    this.disabled = disabled;
  }

  formResetCallback() {
    this.value = this.getAttribute('value') ?? '';
  }
}
